"use client";

import { useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { AlertCircle, ArrowUpRight, Layers, Loader2 } from "lucide-react";
import { Alert } from "@/ui/components/Alert";
import { Avatar } from "@/ui/components/Avatar";
import { Button } from "@/ui/components/Button";
import { EmptyState } from "@/ui/components/EmptyState";
import { Skeleton } from "@/ui/components/Skeleton";
import { Stepper } from "@/ui/components/Stepper";
import type { RecruitingSignal, ResearchRun } from "@/lib/signals/types";
import { RESEARCH_STEPS, candidateEntries, researchStep, formatDate, signalType, type CandidateEntry } from "@/lib/signals/presentation";
import { SignalCard } from "./SignalCard";
import styles from "./SignalsView.module.css";

interface Loadable<T> { data?: T; isPending: boolean; isError: boolean; refetch: () => unknown }

export interface SignalsViewProps {
  runs: Loadable<ResearchRun[]>;
  feed: Loadable<RecruitingSignal[]>;
  selected: string;
  onSelect: (id: string) => void;
  onRetry: (candidateId: number) => void;
  retrying?: number;
  retryFailed?: boolean;
  onFeedback: (id: string, value: -1 | 1 | null) => void;
  feedbackPending?: string;
  feedbackFailed?: boolean;
  evidence: (id: string) => ReactNode;
  /** The candidate's public dossier, shown in their brief. */
  dossier?: (candidateId: string) => ReactNode;
}

const ALL = "all";

function RunIndicator({ entry }: { entry: CandidateEntry }) {
  const visible = entry.signals.filter((signal) => signal.feedback !== -1).length;
  const status = entry.run?.status;
  if (status === "researching") return <span className={styles.researching}><Loader2 size={14} aria-hidden className={styles.spin} />Researching</span>;
  if (status === "queued") return <span className={styles.muted}>Queued</span>;
  if (status === "failed") return <span className={styles.failed}><AlertCircle size={14} aria-hidden />Failed</span>;
  return visible ? <span className={styles.count}>{visible}</span> : <span className={styles.muted}>No signals</span>;
}

function CandidateRow({ entry, active, onSelect }: { entry: CandidateEntry; active: boolean; onSelect: () => void }) {
  const uploaded = formatDate(entry.run?.uploadedAt);
  return <li><button type="button" className={styles.row} aria-current={active ? "true" : undefined} onClick={onSelect}>
    <Avatar size="sm" name={entry.name} />
    <span className={styles.rowText}><span className={styles.rowName}>{entry.name}</span>
      <span className={styles.muted}>{uploaded ? `Uploaded ${uploaded}` : "Earlier research"}</span></span>
    <RunIndicator entry={entry} />
  </button></li>;
}

function ResearchStatus({ run, onRetry, retrying, retryFailed }: { run: ResearchRun; onRetry: () => void; retrying: boolean; retryFailed?: boolean }) {
  if (run.status === "queued") return <div className={styles.status}><p><strong>Waiting to start.</strong> Research begins as soon as a slot is free and usually takes about 10 minutes.</p></div>;
  if (run.status === "researching") {
    const step = researchStep(run.stage);
    return <div className={styles.status} role="status">
      <Stepper steps={RESEARCH_STEPS} activeStep={step} />
      <p className={styles.muted}>Comparing public sources with the CV. This page updates by itself.</p>
    </div>;
  }
  if (run.status === "failed") return <Alert variant="error" title="Research couldn’t finish" action={{ label: retrying ? "Retrying…" : "Retry research", onClick: onRetry }}>
    Retrying continues from where it stopped.{retryFailed && " The last retry didn’t go through. Please try again."}
    {run.error && <span className={styles.reason}>Reason: {run.error}</span>}</Alert>;
  return <p className={styles.muted}>Researched {formatDate(run.finishedAt, true) ?? ""}</p>;
}

export function SignalStack({ signals, onFeedback, feedbackPending, evidence, showSubject }: {
  signals: RecruitingSignal[]; onFeedback: SignalsViewProps["onFeedback"]; feedbackPending?: string;
  evidence: SignalsViewProps["evidence"]; showSubject: boolean;
}) {
  const [showHidden, setShowHidden] = useState(false);
  const [type, setType] = useState<string | null>(null);
  const live = signals.filter((signal) => signal.feedback !== -1);
  const counts = [...live.reduce((map, signal) => map.set(signal.type, (map.get(signal.type) ?? 0) + 1), new Map<string, number>())]
    .sort((a, b) => b[1] - a[1]);
  const ofType = signals.filter((signal) => !type || signal.type === type);
  const hidden = ofType.filter((signal) => signal.feedback === -1);
  const shown = ofType.filter((signal) => showHidden || signal.feedback !== -1);
  return <>
    {showSubject && counts.length > 1 && <div className={styles.typeFilters} role="group" aria-label="Filter by kind">
      {counts.map(([value, count]) => <Button key={value} variant={type === value ? "secondary" : "tertiary"} size="sm" aria-pressed={type === value}
        onClick={() => setType(type === value ? null : value)}>{signalType(value).label} · {count}</Button>)}
    </div>}
    <div className={styles.stack}>{shown.map((signal) => <SignalCard key={signal.id} signal={signal} onFeedback={onFeedback}
      pending={feedbackPending === signal.id} evidence={evidence} showSubject={showSubject} sourcesOpen={!showSubject && shown.length <= 3} />)}</div>
    {hidden.length > 0 && <Button variant="tertiary" size="sm" aria-pressed={showHidden} onClick={() => setShowHidden(!showHidden)}>
      {showHidden ? "Hide dismissed signals" : `Show ${hidden.length} dismissed`}</Button>}
  </>;
}

export function SignalsView(props: SignalsViewProps) {
  const { runs, feed, selected } = props;
  const detailRef = useRef<HTMLElement>(null);
  // Stacked on narrow screens: bring the chosen candidate's brief into view.
  const onSelect = (id: string) => {
    props.onSelect(id);
    if (window.matchMedia?.("(max-width: 1023px)").matches) detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const entries = candidateEntries(runs.data ?? [], feed.data ?? []);
  const recent = entries.filter((entry) => entry.run);
  const earlier = entries.filter((entry) => !entry.run);
  const active = entries.find((entry) => entry.id === selected);
  const live = (feed.data ?? []).filter((signal) => signal.feedback !== -1);
  const fresh = live.filter((signal) => signal.isNew).length;
  const researching = recent.filter((entry) => entry.run?.status === "researching" || entry.run?.status === "queued").length;

  const detail = (() => {
    if (selected !== ALL && active) return <>
      <header className={styles.detailHeader}>
        <Avatar size="lg" name={active.name} />
        <div className={styles.detailTitle}><h2>{active.name}</h2>
          {active.run?.uploadedAt && <p className={styles.muted}>CV uploaded {formatDate(active.run.uploadedAt, true)}</p>}</div>
        {active.uuid && <Link className={styles.profileLink} href={`/candidates/${active.uuid}`}>Open profile<ArrowUpRight size={16} aria-hidden /></Link>}
      </header>
      {active.run && <ResearchStatus run={active.run} onRetry={() => props.onRetry(active.run!.candidateId)}
        retrying={props.retrying === active.run.candidateId} retryFailed={props.retryFailed} />}
      {active.signals.length > 0
        ? <SignalStack signals={active.signals} onFeedback={props.onFeedback} feedbackPending={props.feedbackPending} evidence={props.evidence} showSubject={false} />
        : active.run?.status === "done" && <EmptyState title="Nothing to act on"
          description="Research checked public sources against this CV and found no conflicts, omissions or opportunities worth raising. That’s a normal result." />}
      {props.dossier && active.run?.status !== "queued" && active.run?.status !== "researching" && props.dossier(active.id)}
    </>;
    return <>
      <header className={styles.detailHeader}><div className={styles.detailTitle}><h2>All signals</h2>
        <p className={styles.muted}>{live.length ? "Newest research first. Choose a candidate to see their full research." : ""}</p></div></header>
      {feed.isPending ? <div className={styles.stack}><Skeleton variant="rounded" height={180} /><Skeleton variant="rounded" height={180} /></div>
        : feed.isError ? <Alert variant="error" title="Signals couldn’t be loaded" action={{ label: "Try again", onClick: () => void feed.refetch() }}>Please try again in a moment.</Alert>
        : (feed.data ?? []).length === 0 ? <EmptyState title="No signals yet"
          description="Upload a CV and research starts automatically. Anything worth acting on appears here, usually within 10 minutes." />
        : <SignalStack signals={feed.data ?? []} onFeedback={props.onFeedback} feedbackPending={props.feedbackPending} evidence={props.evidence} showSubject />}
    </>;
  })();

  return <div className={styles.layout}>
    <nav className={styles.rail} aria-label="Researched candidates">
      <p className={styles.summary}>
        {researching > 0 && <span><Loader2 size={14} aria-hidden className={styles.spin} />{researching} in progress</span>}
        <span>{live.length} to review</span>
        {fresh > 0 && <span>{fresh} new since your last visit</span>}
      </p>
      <ul className={styles.rows}>
        <li><button type="button" className={styles.row} aria-current={selected === ALL ? "true" : undefined} onClick={() => onSelect(ALL)}>
          <span className={styles.allIcon}><Layers size={16} aria-hidden /></span>
          <span className={styles.rowText}><span className={styles.rowName}>All signals</span></span>
          {live.length > 0 && <span className={styles.count}>{live.length}</span>}
        </button></li>
      </ul>
      <h2 className={styles.railHeading}>Recent uploads</h2>
      {runs.isPending ? <div className={styles.rows}><Skeleton height={48} /><Skeleton height={48} /></div>
        : runs.isError ? <p className={styles.muted} role="alert">Research status couldn’t be loaded. <Button variant="tertiary" size="sm" onClick={() => void runs.refetch()}>Try again</Button></p>
        : recent.length === 0 ? <p className={styles.muted}>No CVs uploaded in the last 30 days. Upload one and research starts automatically.</p>
        : <ul className={styles.rows}>{recent.map((entry) => <CandidateRow key={entry.id} entry={entry} active={entry.id === selected} onSelect={() => onSelect(entry.id)} />)}</ul>}
      {earlier.length > 0 && <>
        <h2 className={styles.railHeading}>Earlier</h2>
        <ul className={styles.rows}>{earlier.map((entry) => <CandidateRow key={entry.id} entry={entry} active={entry.id === selected} onSelect={() => onSelect(entry.id)} />)}</ul>
      </>}
    </nav>
    <section ref={detailRef} className={styles.detail} aria-live="polite">
      {detail}
      {props.feedbackFailed && <p role="alert" className={styles.failed}>Your feedback wasn’t saved. Please try again.</p>}
    </section>
  </div>;
}
